import { Button, Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { IconDownload, IconPlus, IconUpload } from "@tabler/icons-react";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { usePermission } from "@how/app-kit";
import { DataTable, FilterBar, PageHeader, formatDateOnly, useSearchParamState } from "@how/ui";
import { CrmTabs } from "./CrmTabs";
import {
  CHANNEL_LABELS,
  CONSENT_STATES,
  REPORTED_CHANNELS,
  consentFilterParam,
  consentStateLabel,
  parseConsentFilter,
} from "./consent-queries";
import { PersonDrawer } from "./PersonDrawer";
import { useTags } from "./tag-queries";
import {
  useCreatePerson,
  useExportPeople,
  usePeople,
  type PersonRow,
  type PersonSort,
} from "./queries";

const PAGE_SIZE = 25;

/**
 * Every number on the consent screen as a choice here, grouped by channel, so the list a count opened can be read,
 * changed or cleared in the same place as the other filters (#112). The label carries the channel too, because a
 * closed select shows only the chosen option.
 */
const CONSENT_OPTIONS = REPORTED_CHANNELS.map((channel) => ({
  group: CHANNEL_LABELS[channel],
  items: CONSENT_STATES.map((state) => ({
    value: consentFilterParam({ channel, state }),
    label: `${CHANNEL_LABELS[channel]}: ${consentStateLabel(channel, state).toLowerCase()}`,
  })),
}));
const SORTABLE = new Set<PersonSort["id"]>(["last_name", "created_at"]);

const columns: ColumnDef<PersonRow>[] = [
  {
    accessorKey: "last_name",
    header: "Name",
    cell: (ctx) => {
      const row = ctx.row.original;
      const preferred = row.preferred_name;
      const called = preferred === null || preferred === row.first_name ? "" : ` (${preferred})`;
      return `${row.last_name}, ${row.first_name}${called}`;
    },
  },
  // The name column already carries the first name, so the space goes to how the church actually reaches someone.
  { accessorKey: "email", header: "Email", enableSorting: false },
  { accessorKey: "phone", header: "Phone", enableSorting: false },
  {
    accessorKey: "birthdate",
    header: "Birthdate",
    enableSorting: false,
    meta: { numeric: true },
    // A date-only value: formatted without passing through a time zone, or it shows a day early west of UTC.
    cell: (ctx) => formatDateOnly(ctx.row.original.birthdate),
  },
  {
    accessorKey: "created_at",
    header: "Added",
    meta: { numeric: true },
    cell: (ctx) => new Date(ctx.row.original.created_at).toLocaleDateString(),
  },
];

/** Downloads text the browser already has, with no server round trip and no stored file to expire. */
function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const PORTAL_OPTIONS = [
  { value: "yes", label: "Has a portal login" },
  { value: "no", label: "No portal login" },
];

/**
 * The people directory: the first screen of the core CRM module, built to the list-screen contract in
 * docs/UI_STANDARDS.md. Search, sort and page live in the URL so a filtered list can be shared or reloaded; the
 * server does the filtering, sorting and paging; the record opens in a drawer without leaving the list.
 *
 * Every action is gated by asking the database, never by inferring from a role: the buttons follow
 * `authorize_check`, and the writes behind them are refused again server-side if the answer was wrong.
 */
export function PeoplePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParamState("q");
  const [page, setPage] = useSearchParamState("page", "1");
  const [sortParam, setSortParam] = useSearchParamState("sort", "last_name.asc");
  const [tagParam, setTagParam] = useSearchParamState("tag");
  const [consentParam, setConsentParam] = useSearchParamState("consent");
  const [portalParam, setPortalParam] = useSearchParamState("portal");
  const portalLogin = portalParam === "yes" || portalParam === "no" ? portalParam : undefined;
  const consent = parseConsentFilter(consentParam);
  const tags = useTags();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [sortId, sortDir] = sortParam.split(".");
  const sort: PersonSort = {
    id: SORTABLE.has(sortId as PersonSort["id"]) ? (sortId as PersonSort["id"]) : "last_name",
    desc: sortDir === "desc",
  };
  const pageIndex = Math.max(0, Number.parseInt(page, 10) - 1 || 0);

  const people = usePeople({
    search,
    pageIndex,
    pageSize: PAGE_SIZE,
    sort,
    ...(tagParam.length === 0 ? {} : { tagId: tagParam }),
    ...(consent === null ? {} : { consent }),
    ...(portalLogin === undefined ? {} : { portalLogin }),
  });
  const filtering =
    search.trim().length > 0 ||
    tagParam.length > 0 ||
    consent !== null ||
    portalLogin !== undefined;
  const canWrite = usePermission("people.write", { write: true });
  const canExport = usePermission("people.export");
  const canImport = usePermission("people.import", { write: true });
  const createPerson = useCreatePerson();
  const exportPeople = useExportPeople();

  const sorting = useMemo<SortingState>(
    () => [{ id: sort.id, desc: sort.desc }],
    [sort.id, sort.desc],
  );
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: PAGE_SIZE }),
    [pageIndex],
  );

  const status = people.isPending ? "loading" : people.isError ? "error" : "ready";
  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0;

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone your church knows, whether or not they have a login."
        actions={
          <Group gap="sm">
            {canImport.data === true ? (
              <Button
                variant="light"
                leftSection={<IconUpload size={16} />}
                onClick={() => {
                  void navigate("/people/import");
                }}
              >
                Import
              </Button>
            ) : null}
            {canExport.data === true ? (
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                loading={exportPeople.isPending}
                onClick={() => {
                  exportPeople.mutate(search, {
                    onSuccess: (csv) => {
                      downloadCsv("people.csv", csv);
                    },
                  });
                }}
              >
                Export
              </Button>
            ) : null}
            {canWrite.data === true ? (
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setAdding(true);
                }}
              >
                Add person
              </Button>
            ) : null}
          </Group>
        }
      />
      <CrmTabs active="people" />
      <Stack gap="xl">
        <div>
          <FilterBar
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(undefined);
            }}
            searchPlaceholder="Search by name"
            filters={
              // A fragment, not a group: the filter bar stacks its direct children full width on a phone.
              <>
                <Select
                  aria-label="Filter by tag"
                  placeholder="Any tag"
                  clearable
                  data={(tags.data ?? []).map((tag) => ({ value: tag.id, label: tag.name }))}
                  value={tagParam.length === 0 ? null : tagParam}
                  onChange={(value) => {
                    setTagParam(value ?? undefined);
                    setPage(undefined);
                  }}
                  w={200}
                />
                <Select
                  aria-label="Filter by consent"
                  placeholder="Any consent"
                  clearable
                  data={CONSENT_OPTIONS}
                  value={consent === null ? null : consentFilterParam(consent)}
                  onChange={(value) => {
                    setConsentParam(value ?? undefined);
                    setPage(undefined);
                  }}
                  w={300}
                />
                <Select
                  aria-label="Filter by portal login"
                  placeholder="Any portal login"
                  clearable
                  data={PORTAL_OPTIONS}
                  value={portalLogin ?? null}
                  onChange={(value) => {
                    setPortalParam(value ?? undefined);
                    setPage(undefined);
                  }}
                  w={200}
                />
              </>
            }
          />
          <DataTable
            label="People"
            columns={columns}
            rows={people.data?.rows ?? []}
            totalRows={people.data?.total ?? 0}
            status={status}
            pagination={pagination}
            onPaginationChange={(updater) => {
              const next = typeof updater === "function" ? updater(pagination) : updater;
              setPage(String(next.pageIndex + 1));
            }}
            sorting={sorting}
            onSortingChange={(updater) => {
              const next = typeof updater === "function" ? updater(sorting) : updater;
              const first = next[0];
              setSortParam(
                first === undefined ? undefined : `${first.id}.${first.desc ? "desc" : "asc"}`,
              );
            }}
            getRowId={(row) => row.id}
            onRowClick={(row) => {
              setSelected(row.id);
            }}
            emptyTitle={filtering ? "Nobody matches" : "No people yet"}
            emptyDescription={
              filtering
                ? "Try a different name, tag or consent filter."
                : "Add someone, or bring in a list with Import."
            }
            errorMessage="Could not load people."
            onRetry={() => {
              void people.refetch();
            }}
          />
        </div>
      </Stack>

      <PersonDrawer
        personId={selected}
        onClose={() => {
          setSelected(null);
        }}
      />

      <Modal
        opened={adding}
        onClose={() => {
          setAdding(false);
        }}
        title="Add a person"
        radius="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="First name"
            required
            value={firstName}
            onChange={(event) => {
              setFirstName(event.currentTarget.value);
            }}
          />
          <TextInput
            label="Last name"
            required
            value={lastName}
            onChange={(event) => {
              setLastName(event.currentTarget.value);
            }}
          />
          <TextInput
            label="Email"
            description="Optional. A person does not need one, and never needs a login."
            value={email}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
            }}
          />
          <Group justify="flex-end" mt="xs">
            <Button
              variant="subtle"
              onClick={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!canSubmit}
              loading={createPerson.isPending}
              onClick={() => {
                createPerson.mutate(
                  {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    ...(email.trim().length === 0 ? {} : { email: email.trim() }),
                  },
                  {
                    onSuccess: (personId) => {
                      setAdding(false);
                      setFirstName("");
                      setLastName("");
                      setEmail("");
                      // Open the record straight away: the next thing anyone does is add a note or a tag.
                      setSelected(personId);
                    },
                  },
                );
              }}
            >
              Add person
            </Button>
          </Group>
          {createPerson.isError ? (
            <Text size="sm" c="red">
              Could not add this person. Check your access and try again.
            </Text>
          ) : null}
        </Stack>
      </Modal>
    </>
  );
}
